import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2 } from "lucide-react";

import { api } from "@/lib/api";
import { useToast } from "@/components/toast";
import { AdminPage, Card, Meta } from "@/components/admin-ui";
import { Button, ErrorState, Loading } from "@/components/ui";

// Documents are decrypted by the backend for the lock holder only and rendered from memory (never cached/stored).
function SecureDoc({ docId, label }) {
  const [uri, setUri] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!docId) return;
    api(`/review/documents/${docId}`).then((d) => setUri(`data:${d.content_type};base64,${d.data_base64}`)).catch((e) => setErr(e.message));
  }, [docId]);
  return (
    <Card testId={`review-doc-${label.toLowerCase().replace(/\W+/g, "-")}`}>
      <p className="mb-2 text-sm font-bold">{label}</p>
      <div className="flex aspect-[4/3] items-center justify-center overflow-hidden rounded-xl bg-[#F4EBE6]">
        {!docId ? <Meta>Not provided</Meta> : err ? <Meta>{err}</Meta> : uri ? <img src={uri} alt={label} className="h-full w-full object-contain" /> : <Loader2 className="h-6 w-6 animate-spin text-primary" />}
      </div>
    </Card>
  );
}

export default function ReviewDetail() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const { id } = useParams();
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(null);
  const decided = useRef(false);
  // Leaving the page releases the lock unless a decision was made
  useEffect(() => () => { if (!decided.current) api(`/review/applications/${id}/release`, { method: "POST" }).catch(() => {}); }, [id]);
  const { data: a, isLoading, isError, error, refetch } = useQuery({ queryKey: ["review", id], queryFn: () => api(`/review/applications/${id}`), gcTime: 0, staleTime: 0 });

  const decide = async (decision) => {
    setBusy(decision);
    try {
      await api(`/review/applications/${id}/decision`, { method: "POST", body: { decision, notes: notes || null } });
      decided.current = true;
      toast(decision === "approve" ? "Approved — member is now verified" : decision === "reject" ? "Rejected" : "More info requested", "success");
      qc.invalidateQueries({ queryKey: ["review-queue"] });
      navigate("/review");
    } catch (e) { toast(e.message, "error"); } finally { setBusy(null); }
  };
  const release = async () => {
    await api(`/review/applications/${id}/release`, { method: "POST" }).catch(() => {});
    decided.current = true;
    qc.invalidateQueries({ queryKey: ["review-queue"] });
    navigate("/review");
  };

  if (isLoading) return <Loading />;
  if (isError || !a) return <AdminPage title="Application" testId="review-detail-screen"><ErrorState testId="review-detail-error" message={error?.message ?? ""} onRetry={() => refetch()} /></AdminPage>;
  return (
    <AdminPage title="Application" testId="review-detail-screen">
      <Card testId="review-member-info">
        <p className="font-display text-[26px]">{a.member?.full_name}</p>
        <Meta>Display name {a.member?.display_name} · DOB {a.member?.dob} ({a.member?.age}) · {a.member?.city}, {a.member?.country}</Meta>
        <Meta testId="review-status">Payment {a.payment_reference} · status {a.verification_status}</Meta>
      </Card>
      <div className="grid gap-3 md:grid-cols-3">
        <SecureDoc docId={a.document_ids.id_front} label="ID front" />
        <SecureDoc docId={a.document_ids.id_back} label="ID back" />
        <SecureDoc docId={a.document_ids.selfie} label="Selfie" />
      </div>
      <label htmlFor="review-notes" className="mt-2 text-sm font-bold">Notes to member (required for reject / more info)</label>
      <textarea id="review-notes" data-testid="review-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. ID photo is blurry, please re-upload"
        className="min-h-[100px] w-full rounded-xl border border-border bg-white p-3 text-[15px] focus:border-primary focus:outline-none" />
      {a.verification_status === "under_review" ? (
        <div className="flex flex-wrap gap-3">
          <Button testId="review-approve-button" title="Approve" icon={CheckCircle2} loading={busy === "approve"} onClick={() => decide("approve")} />
          <Button testId="review-more-info-button" title="Request more info" variant="outline" loading={busy === "more_info"} onClick={() => decide("more_info")} />
          <Button testId="review-reject-button" title="Reject" variant="danger" loading={busy === "reject"} onClick={() => decide("reject")} />
          <Button testId="review-release-button" title="Release lock & go back" variant="ghost" onClick={release} />
        </div>
      ) : <Meta testId="review-already-decided">Already decided.</Meta>}
    </AdminPage>
  );
}
